import {
  AUTOMATION_CATEGORY_NAME_MAX_LENGTH,
  AUTOMATION_CATEGORY_PRESET_COLORS,
  type AutomationCategoryResponse,
} from '@reach/shared-types/automation-category';
import { X } from 'lucide-react';
import { type CSSProperties, type FormEvent, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '../../../../components/ui/button';
import { CategoryDeleteWarning } from './CategoryDeleteWarning';
import { CATEGORY_EDIT_DIALOG_ATTRIBUTE } from './categoryEditDialogTarget';
import styles from './CategoryEditDialog.module.css';
import { useCategoryEditor } from './useCategoryEditor';

interface CategoryEditDialogProps {
  category: AutomationCategoryResponse;
  onClose: () => void;
  onDeleted?: () => void;
}

/** Rename, recolor, or delete a partner category. Changes apply everywhere it is used. */
export function CategoryEditDialog({ category, onClose, onDeleted }: CategoryEditDialogProps) {
  const [name, setName] = useState(category.name);
  const [color, setColor] = useState(category.color);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const { save, remove, isPending } = useCategoryEditor({
    categoryId: category.id,
    onDone: onClose,
    onDeleted,
  });

  const trimmedName = name.trim();
  const nameIsValid = trimmedName.length > 0;
  const deleteLabel = isConfirmingDelete ? 'Confirm delete' : 'Delete';

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!nameIsValid) return;
    save({ name: trimmedName, color });
  };

  const handleDelete = () => {
    if (!isConfirmingDelete) {
      setIsConfirmingDelete(true);
      return;
    }
    remove();
  };

  const handleClose = () => {
    if (!isPending) onClose();
  };

  // Portaled so it isn't positioned inside the transformed settings dialog it can open from.
  return createPortal(
    <>
      <div
        className={styles.backdrop}
        onClick={handleClose}
        {...{ [CATEGORY_EDIT_DIALOG_ATTRIBUTE]: '' }}
      />
      <dialog
        className={styles.dialog}
        open
        aria-labelledby="category-edit-title"
        {...{ [CATEGORY_EDIT_DIALOG_ATTRIBUTE]: '' }}>
        <header>
          <hgroup>
            <h2 id="category-edit-title">Edit category</h2>
            <p>Changes apply to every automation using it, including tenants’ custom automations.</p>
          </hgroup>
          <button type="button" aria-label="Close" disabled={isPending} onClick={handleClose}>
            <X aria-hidden />
          </button>
        </header>
        <form onSubmit={handleSubmit}>
          <label>
            Name
            <input
              type="text"
              value={name}
              maxLength={AUTOMATION_CATEGORY_NAME_MAX_LENGTH}
              onChange={event => setName(event.target.value)}
              autoFocus
            />
          </label>
          <fieldset className={styles.colors}>
            <legend>Color</legend>
            {AUTOMATION_CATEGORY_PRESET_COLORS.map(presetColor => (
              <button
                key={presetColor}
                type="button"
                aria-label={`Color ${presetColor}`}
                aria-pressed={presetColor.toLowerCase() === color.toLowerCase()}
                style={{ '--category-color': presetColor } as CSSProperties}
                onClick={() => setColor(presetColor)}
              />
            ))}
            <label className={styles.customColor}>
              Custom
              <input type="color" value={color} onChange={event => setColor(event.target.value)} />
            </label>
          </fieldset>
          {isConfirmingDelete && (
            <CategoryDeleteWarning categoryId={category.id} categoryName={category.name} />
          )}
          <footer>
            <Button type="button" variant="destructive" disabled={isPending} onClick={handleDelete}>
              {deleteLabel}
            </Button>
            <Button type="button" variant="outline" disabled={isPending} onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isPending || !nameIsValid}>
              Save
            </Button>
          </footer>
        </form>
      </dialog>
    </>,
    document.body,
  );
}
